import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Truck, Search, Trash2 } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

const Suppliers = ({ user }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    rnc: '',
    telefono: '',
    email: '',
    direccion: '',
    contacto: '',
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/suppliers`, formData);
      toast.success('Proveedor creado exitosamente');
      setDialogOpen(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error('Error al crear proveedor');
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (window.confirm('¿Está seguro de eliminar este proveedor?')) {
      try {
        await axios.delete(`${API}/suppliers/${supplierId}`);
        toast.success('Proveedor eliminado');
        fetchSuppliers();
      } catch (error) {
        toast.error('Error al eliminar proveedor');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      rnc: '',
      telefono: '',
      email: '',
      direccion: '',
      contacto: '',
    });
  };

  const filteredSuppliers = suppliers.filter(
    (s) =>
      s.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.rnc && s.rnc.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-slate-800">Proveedores</h1>
          <p className="text-slate-600 mt-1">Gestiona los proveedores del negocio</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="create-supplier-btn">
              <Truck className="mr-2" size={18} />
              Nuevo Proveedor
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid="create-supplier-dialog">
            <DialogHeader>
              <DialogTitle>Crear Nuevo Proveedor</DialogTitle>
              <DialogDescription>Complete la información del proveedor</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateSupplier} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="nombre">Nombre *</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    required
                    data-testid="form-nombre-input"
                  />
                </div>
                <div>
                  <Label htmlFor="rnc">RNC</Label>
                  <Input
                    id="rnc"
                    value={formData.rnc}
                    onChange={(e) => setFormData({ ...formData, rnc: e.target.value })}
                    data-testid="form-rnc-input"
                  />
                </div>
                <div>
                  <Label htmlFor="telefono">Teléfono</Label>
                  <Input
                    id="telefono"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    data-testid="form-telefono-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="form-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contacto">Contacto</Label>
                  <Input
                    id="contacto"
                    value={formData.contacto}
                    onChange={(e) => setFormData({ ...formData, contacto: e.target.value })}
                    data-testid="form-contacto-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="direccion">Dirección</Label>
                  <Input
                    id="direccion"
                    value={formData.direccion}
                    onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                    data-testid="form-direccion-input"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="submit-supplier-btn">
                Crear Proveedor
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
        <Input
          placeholder="Buscar proveedores..."
          className="pl-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="search-suppliers-input"
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <table className="custom-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>RNC</th>
              <th>Teléfono</th>
              <th>Email</th>
              <th>Contacto</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody data-testid="suppliers-table-body">
            {filteredSuppliers.map((supplier) => (
              <tr key={supplier.id} data-testid={`supplier-row-${supplier.id}`}>
                <td className="font-medium">{supplier.nombre}</td>
                <td>{supplier.rnc || '-'}</td>
                <td>{supplier.telefono || '-'}</td>
                <td className="text-slate-600">{supplier.email || '-'}</td>
                <td>{supplier.contacto || '-'}</td>
                <td>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDeleteSupplier(supplier.id)}
                    data-testid={`delete-supplier-btn-${supplier.id}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredSuppliers.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            No se encontraron proveedores
          </div>
        )}
      </div>
    </div>
  );
};

export default Suppliers;
